import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { InviteBanner } from "@/components/auth/InviteBanner";
import { PhoneStep } from "@/components/auth/PhoneStep";
import { OtpStep } from "@/components/auth/OtpStep";
import { AuthSplitLayout } from "@/components/auth/AuthSplitLayout";
import { useAuthFlow } from "@/lib/useAuthFlow";
import { AuthMethodToggle, EmailPasswordForm } from "@/components/auth/EmailPasswordForm";
import { VoiceHelpButton } from "@/components/auth/VoiceHelpButton";
import { toast } from "sonner";

/* Login — split-screen auth (Login.pdf reference). Reuses the existing
 * phone + OTP + adaptive-captcha flow via useAuthFlow / PhoneStep / OtpStep. */
export default function Login() {
    const navigate = useNavigate();
    const { user, login } = useAuth();
    const { t } = useLang();

    const [method, setMethod] = useState("phone");
    const location = useLocation();
    const nextRaw = new URLSearchParams(location.search).get("next") || "";
    const nextPath = nextRaw.startsWith("/") && !nextRaw.startsWith("//") ? nextRaw : "/dashboard";
    const flow = useAuthFlow({
        mode: "login",
        onSuccess: (u) => {
            login(u);
            toast.success(`Welcome back${u.name ? `, ${u.name}` : ""}`);
            navigate(nextPath, { replace: true });
        },
    });

    useEffect(() => {
        if (user) navigate(nextPath, { replace: true });
    }, [user, navigate, nextPath]);

    const otpSub = `${t("otpSentTo")} +91 ${flow.phone}. ${t("expiresIn")} ${Math.floor(
        flow.secondsLeft / 60
    )}:${String(flow.secondsLeft % 60).padStart(2, "0")}.`;

    return (
        <AuthSplitLayout
            testIdPrefix="login"
            heroHeading="Start your family legacy today."
            heroText="Create a safe, warm, and evergreen space to preserve your family's memories, stories, and heritage across generations."
        >
            <VoiceHelpButton
                helpKey={flow.step === "phone" ? "login_phone" : "otp"}
                className="absolute top-3 right-3 z-20 scale-90 origin-top-right"
            />

            <div className="text-center mb-5">
                <h1 data-testid="login-title" className="text-[26px] sm:text-[28px] font-bold text-[#1B5E20] leading-tight">
                    {flow.step === "phone" ? "Welcome back" : t("otpTitle")}
                </h1>
                <p className="mt-1.5 text-[14px] text-[#666]">
                    {flow.step === "phone" ? "Log in to continue preserving your heritage." : otpSub}
                </p>
            </div>

            {flow.invitePreview && (
                <div className="mb-4">
                    <InviteBanner
                        invitePreview={flow.invitePreview}
                        onRemove={() => {
                            flow.setInviteCode("");
                            flow.setInvitePreview(null);
                        }}
                    />
                </div>
            )}

            {flow.step === "phone" && (
                <AuthMethodToggle method={method} onChange={setMethod} prefix="login" />
            )}

            {flow.step === "phone" && method === "email" ? (
                <EmailPasswordForm
                    mode="login"
                    prefix="login"
                    inviteCode={flow.inviteCode}
                    onSuccess={(u) => {
                        login(u);
                        toast.success(`Welcome back${u.name ? `, ${u.name}` : ""}`);
                        navigate(nextPath, { replace: true });
                    }}
                />
            ) : flow.step === "phone" ? (
                <PhoneStep
                    t={t}
                    phone={flow.phone}
                    setPhone={flow.setPhone}
                    invitePreview={flow.invitePreview}
                    sending={flow.sending}
                    onSubmit={flow.requestOtp}
                    showName={false}
                    submitLabelKey="sendOtp"
                    captchaToken={flow.captchaToken}
                    setCaptchaToken={flow.setCaptchaToken}
                    captchaResetSignal={flow.captchaResetSignal}
                    captchaRequired={flow.captchaRequired}
                />
            ) : (
                <OtpStep
                    t={t}
                    code={flow.code}
                    setCode={flow.setCode}
                    devOtp={flow.devOtp}
                    verifying={flow.verifying}
                    sending={flow.sending}
                    secondsLeft={flow.secondsLeft}
                    resendLeft={flow.resendLeft}
                    onSubmit={flow.verifyOtp}
                    onChangeNumber={flow.changeNumber}
                    onResend={flow.requestOtp}
                    accountPreview={flow.accountPreview}
                />
            )}

            {flow.step === "phone" && (
                <>
                    <label
                        className="mt-4 flex items-center gap-2.5 cursor-pointer select-none"
                        data-testid="login-remember-me-label"
                    >
                        <input
                            type="checkbox"
                            data-testid="login-remember-me"
                            checked={flow.rememberMe}
                            onChange={(e) => flow.setRememberMe(e.target.checked)}
                            className="h-4 w-4 rounded border-[#CFCFCF] accent-[#1B5E20]"
                        />
                        <span className="text-[13.5px] text-[#4A5D4E]">
                            Keep me signed in for 30 days
                        </span>
                    </label>

                    <div className="my-5 flex items-center gap-3" aria-hidden>
                        <span className="h-px flex-1 bg-[#E7E2D3]" />
                        <span className="text-[12px] uppercase tracking-wide text-[#9A937F]">or</span>
                        <span className="h-px flex-1 bg-[#E7E2D3]" />
                    </div>

                    <p className="text-center text-[14px] text-[#4A5D4E]">
                        Don&apos;t have an account?{" "}
                        <Link data-testid="login-go-signup" to="/signup" className="text-[#DC2626] font-bold hover:underline">
                            Sign up
                        </Link>
                    </p>

                    <p className="mt-3 text-center text-[12.5px]">
                        <Link data-testid="login-go-recovery" to="/account-recovery" className="text-[#6B7A6E] hover:text-[#1B5E20] hover:underline">
                            Lost access to your phone number? Recover account →
                        </Link>
                    </p>
                </>
            )}
        </AuthSplitLayout>
    );
}
